import type { Plugin } from "../../core/plugin";
import { PluginRegistry } from "../../core/pluginRegistry";

interface Room {
  adults: number;
  children: number;
}

export class RoomSelector implements Plugin {
  private rooms: Room[] = [{ adults: 1, children: 0 }];

  private readonly roomsContainer: HTMLElement;
  private readonly deleteButtons: HTMLButtonElement[] = [];

  constructor(private readonly element: HTMLElement) {
    this.roomsContainer = this.element.querySelector(
      "[data-rooms-container]",
    ) as HTMLElement;

    if (!this.roomsContainer) {
      throw new Error("Rooms container not found");
    }
  }

  init(): void {
    this.bindEvents();
    this.updateRoomsDisplay();
  }

  destroy(): void {
    this.deleteButtons.forEach((button) => {
      button.removeEventListener("click", this.handleDeleteRoom);
    });
  }

  private bindEvents(): void {
    // Listen for room updates from the parent form
    this.element.addEventListener(
      "roomsUpdated",
      this.handleRoomsUpdated as EventListener,
    );
  }

  private readonly handleRoomsUpdated = (event: Event): void => {
    const customEvent = event as CustomEvent;
    this.rooms = customEvent.detail.rooms;
    this.updateRoomsDisplay();
  };

  private readonly handleDeleteRoom = (event: Event): void => {
    const button = event.target as HTMLButtonElement;
    const roomElement = button.closest("[data-room-index]") as HTMLElement;
    const roomIndex = parseInt(roomElement.dataset.roomIndex || "0");

    if (roomIndex > 0) {
      this.rooms.splice(roomIndex, 1);
      this.updateRoomsDisplay();

      // Emit event to notify parent
      const updateEvent = new CustomEvent("roomsUpdated", {
        detail: { rooms: this.rooms },
      });
      this.element.dispatchEvent(updateEvent);
    }
  };

  private updateRoomsDisplay(): void {
    // Clear existing delete button listeners
    this.deleteButtons.forEach((button) => {
      button.removeEventListener("click", this.handleDeleteRoom);
    });
    this.deleteButtons.length = 0;

    // Update room numbers and delete button visibility
    const roomElements =
      this.roomsContainer.querySelectorAll("[data-room-index]");

    roomElements.forEach((roomElement, index) => {
      const htmlElement = roomElement as HTMLElement;
      const roomNumber = htmlElement.querySelector("[data-room-number]");
      const deleteButton = htmlElement.querySelector(
        '[data-action="delete-room"]',
      ) as HTMLButtonElement;

      if (roomNumber) {
        roomNumber.textContent = (index + 1).toString();
      }

      if (deleteButton) {
        htmlElement.dataset.roomIndex = index.toString();

        if (index > 0) {
          deleteButton.style.display = "block";
          deleteButton.addEventListener("click", this.handleDeleteRoom);
          this.deleteButtons.push(deleteButton);
        } else {
          deleteButton.style.display = "none";
        }
      }
    });
  }
}

PluginRegistry.register("room-selector", RoomSelector);
